import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonBlockquote,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuSelectTextAlign,
  RichTextEditorProvider,
  RichTextField,
} from "mui-tiptap";

import { Typography, type SxProps, useTheme } from '@mui/material';
import {
  FIELD_RADIUS,
  FieldLabel,
  fieldAccentColor,
  fieldBorderColor,
  fieldErrorColor,
  fieldErrorRing,
  fieldFill,
  fieldFocusRing,
  fieldHoverRing,
} from '@repo/ui';
import React from 'react';

interface RichTextEditorFieldProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: string | number;
  sx?: SxProps;
}

export const RichTextEditorField = (props: RichTextEditorFieldProps) => {
  const { 
    label, 
    value = "", 
    onChange, 
    error = false, 
    helperText, 
    placeholder = "Start typing...",
    disabled = false,
    minHeight = '300px',
    sx
  } = props;
  
  const theme = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        HTMLAttributes: {
          class: 'rich-text-image',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  // Migrated onto `fieldChrome` (§31 — one source for a field's border/fill/focus)
  // rather than this component's own hand-picked `primary[50]`/`primary[100]`
  // literals, which had drifted from every other field: no hover glow at all (the
  // gap this pass closes everywhere), a disabled-hover border pinned to the FILL
  // token (`primary[50]`, almost invisible) rather than the border token, and an
  // errored field's focus ring set to `'none'` instead of being recoloured red —
  // the exact bug the sibling `Input`/`TextField` components already had fixed
  // (see their own comments): the error state read as LESS interactive than a
  // healthy one instead of just a different colour.
  const vera_theme: SxProps = {
    backgroundColor: fieldFill(theme),
    minHeight: minHeight,
    borderRadius: `${FIELD_RADIUS}px`,
    border: `1px solid ${error ? fieldErrorColor(theme) : fieldBorderColor(theme)}`,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    "&:hover": {
      border: `1px solid ${
        disabled
          ? fieldBorderColor(theme)
          : error
            ? fieldErrorColor(theme)
            : fieldAccentColor(theme)
      }`,
      // No glow for `disabled` (can't be typed into) or `error` (the state a field
      // is IN outranks the state it is being touched in — same rule as `Input`).
      boxShadow: disabled || error ? 'none' : fieldHoverRing(theme),
    },
    "&:focus-within": {
      border: `1px solid ${error ? fieldErrorColor(theme) : fieldAccentColor(theme)}`,
      boxShadow: error ? fieldErrorRing(theme) : fieldFocusRing(theme),
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
    "& .MuiTiptap-FieldContainer-notchedOutline": {
      border: "none !important",
    },
    "& .MuiTiptap-MenuBar-root": {
      position: "static !important",
      backgroundColor: theme.palette.primary[50],
    },
    "& .rich-text-image": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: 1,
    },
    "& .ProseMirror": {
      padding: '12px',
      minHeight: `calc(${minHeight} - 24px)`,
      fontSize: 16,
      "&:focus": {
        outline: "none",
      },
      "& p.is-editor-empty:first-child::before": {
        color: theme.palette.text.disabled,
        content: `"${placeholder}"`,
        float: "left",
        height: 0,
        pointerEvents: "none",
      },
    },
    // mui-tiptap's RichTextContent-root defaults the editor body to 20px at the
    // same specificity as `& .ProseMirror`, so it wins on source order. Match the
    // standard input font size (16px) with a higher-specificity selector.
    "& .MuiTiptap-RichTextContent-root .ProseMirror": {
      fontSize: 16,
    },
    ...sx
  };

  // Update editor content when value prop changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  return (
    <div>
      <FieldLabel label={label} />
      <RichTextEditorProvider editor={editor}>
        <RichTextField 
          variant="outlined"
          sx={vera_theme} 
          controls={
            <MenuControlsContainer>
              <MenuSelectHeading />
              <MenuDivider />
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonCode />
              <MenuButtonCodeBlock />
              <MenuDivider />
              <MenuButtonBulletedList />
              <MenuButtonOrderedList />
              <MenuButtonBlockquote />
              <MenuDivider />
              <MenuSelectTextAlign />
            </MenuControlsContainer>
          }
        />
      </RichTextEditorProvider>
      {helperText && (
        <Typography 
          variant="caption" 
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 1, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </div>
  );
};
